import {useCallback, useEffect, useMemo} from 'react';
import {
  useNavigate,
  useLocation,
  type NavigateFunction,
} from 'react-router-dom';

import {
  type PaletteMode,
  useTheme,
  type Theme,
  type SxProps,
} from '@mui/material/styles';
import {useAuth} from 'oidc-react';

import {type NavigationLink} from '../constants/navigation';
import {
  SIDE_NAVIGATION_COLLAPSED_WIDTH,
  SIDE_NAVIGATION_EXPANDED_WIDTH,
} from '../constants/SideNavigationWidth';
import {useDialog} from '../hooks/useDialog';
import {useAuthStore} from '../stores/authStore';
import {useNavigationStore} from '../stores/navigationStore';
import {useOrganisationStore} from '../stores/organisationStore';
import {usePreferencesStore} from '../stores/preferencesStore';

interface UseSideNavigationHookParams {
  onMobileToggle: () => void;
  isMobile: boolean;
  mobileOpen: boolean;
}

export const useSideNavigation = ({
  onMobileToggle,
  isMobile,
  mobileOpen,
}: UseSideNavigationHookParams): {
  navigate: NavigateFunction;
  themeMode: PaletteMode;
  toggleSidePanelExpanded: () => void;
  computedWidth: string;
  validLinks: NavigationLink[];
  handleLogout: () => Promise<void>;
  logoutDialogOpen: boolean;
  onOpenLogoutDialog: () => void;
  onCloseLogoutDialog: () => void;
  iconSx: SxProps<Theme>;
  sidePanelExpanded: boolean;
  theme: Theme;
  organisationName: string;
  organisationGroupName: string;
  locationPathname: string;
} => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const {signOutRedirect, signOut} = useAuth();
  const {
    isOpen: logoutDialogOpen,
    onOpen: onOpenLogoutDialog,
    onClose: onCloseLogoutDialog,
  } = useDialog();
  const logout = useAuthStore((state) => state.logout);

  const links = useNavigationStore((state) => state.links);
  const checkRoles = useAuthStore((state) => state.checkRoles);
  const themeMode = usePreferencesStore((state) => state.themeMode);
  const sidePanelExpanded = usePreferencesStore(
    (state) => state.sidePanelExpanded,
  );
  const toggleSidePanelExpanded = usePreferencesStore(
    (state) => state.toggleSidePanelExpanded,
  );

  const organisationName = useOrganisationStore(
    (state) => state.organisationName,
  );
  const organisationGroupName = useOrganisationStore(
    (state) => state.organisationGroupName,
  );

  const computedWidth = sidePanelExpanded
    ? SIDE_NAVIGATION_EXPANDED_WIDTH
    : SIDE_NAVIGATION_COLLAPSED_WIDTH;

  const filterLink = useCallback(
    (link: NavigationLink): boolean => {
      if (link.conditionalRoles) {
        return link.conditionalRoles.some((r) => checkRoles(...r));
      }
      return checkRoles(...link.roles) || location.pathname === link.path;
    },
    [checkRoles, location.pathname],
  );

  const validLinks = useMemo(
    () =>
      links.filter(filterLink).map((link) => ({
        ...link,
        children: link.children?.filter(filterLink),
      })),
    [links, filterLink],
  );

  useEffect(() => {
    if (!isMobile && mobileOpen) onMobileToggle();
  }, [isMobile, mobileOpen, onMobileToggle]);

  const handleLogout = useCallback(async () => {
    logout();
    await signOutRedirect({redirectMethod: 'replace'});
    await signOut();
  }, [logout, signOutRedirect, signOut]);

  const iconSx = {
    borderRadius: 2,
    mx: sidePanelExpanded ? 2 : 1,
    mb: 0.5,
    '&.Mui-selected': {
      backgroundColor: theme.palette.action.selected,
      '&:hover': {backgroundColor: theme.palette.action.hover},
    },
    '&:hover': {backgroundColor: theme.palette.action.hover},
  };

  return {
    navigate,
    themeMode,
    toggleSidePanelExpanded,
    computedWidth,
    validLinks,
    handleLogout,
    logoutDialogOpen,
    onOpenLogoutDialog,
    onCloseLogoutDialog,
    iconSx,
    sidePanelExpanded,
    theme,
    organisationName,
    organisationGroupName,
    locationPathname: location.pathname,
  };
};
